import { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { toMin } from "../../global/utility/statisticsUtil";
import {
  CHART_FONT_FAMILY,
  TOOLTIP_STYLE,
  chartTextStyle,
  buildChartTitle,
  buildLegend,
  buildSliderDataZoom,
  SLIDER_LABEL_EVENTS,
  CHART_GRID_BOTTOM,
  buildYAxisNameGap,
  formatAxisValue,
  createChartToolbox,
  CHART_GRID_RIGHT_PADDING,
} from "./ApacheChartUtils";

const ApacheStackedBarChart = ({
  title = "",
  xAxisData = [],
  tooltipXAxisData = [],
  series = [],
  height = "400px",
  loading = false,
  showLegend = true,
}) => {
  const chartRef = useRef(null);
  const option = useMemo(() => {
    const xData = xAxisData?.data ?? xAxisData;

    const maxStackedVal = (() => {
      const len = (series[0]?.data ?? []).length;
      let max = 0;
      for (let i = 0; i < len; i++) {
        const sum = series.reduce((acc, s) => acc + (Math.abs(s?.data?.[i]) || 0), 0);
        if (sum > max) max = sum;
      }
      return max;
    })();

    const yAxisNameGap = buildYAxisNameGap(maxStackedVal, xAxisData?.toolTipUnit);

    return {
      textStyle: chartTextStyle,
      title: buildChartTitle(title),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "none" },
        textStyle: { ...chartTextStyle, color: "#000000" },
        formatter: (params) => {
          const idx = params[0]?.dataIndex;
          const rawLabel = tooltipXAxisData[idx];
          const label =
            rawLabel && rawLabel !== "Invalid date"
              ? rawLabel
              : xData?.[idx] ?? params[0]?.axisValue;
          const isMinutes = xAxisData?.toolTipUnit === " minute(s)";
          const isPercent = xAxisData?.toolTipUnit === "Percent";
          const formatValue = (val) => {
            if (isMinutes) return toMin(val);
            if (isPercent) return `${val}%`;
            return `${val}${xAxisData?.toolTipUnit ? ` ${xAxisData.toolTipUnit}` : ""}`;
          };
          const rows = params
            .map((p) => `<div style="${TOOLTIP_STYLE}">${p.marker}${p.seriesName}: <b>${formatValue(Math.abs(p.value))}</b></div>`)
            .join("");
          return `<div style="${TOOLTIP_STYLE}">${label}</div>${rows}`;
        },
      },
      legend: buildLegend(showLegend),
      toolbox: createChartToolbox(chartRef),
      grid: {
        left: "5%",
        right: CHART_GRID_RIGHT_PADDING,
        bottom: CHART_GRID_BOTTOM,
        top: title ? 60 : showLegend ? 40 : 20,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xData,
        name: xAxisData?.xName ?? "Time",
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: { rotate: 0, fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        name: xAxisData?.yName ?? "",
        nameLocation: "middle",
        nameGap: yAxisNameGap,
        nameRotate: 90,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: {
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          formatter: (val) => formatAxisValue(val, xAxisData?.toolTipUnit),
        },
        splitLine: { show: true },
      },
      dataZoom: buildSliderDataZoom(0, "x"),
      series: series.map((s) => ({
        name: s?.name,
        type: "bar",
        stack: "total",
        data: s?.data,
        color: s?.color,
        barMaxWidth: 40,
        itemStyle: {
          borderRadius: s?.isTopSeries ? [4, 4, 0, 0] : [0, 0, 0, 0],
        },
        emphasis: { focus: "series" },
        label: {
          show: s?.showLabel ?? false,
          position: "inside",
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          color: "#ffffff",
        },
      })),
    };
  }, [title, xAxisData, tooltipXAxisData, series, showLegend]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={SLIDER_LABEL_EVENTS}
    />
  );
};

export default ApacheStackedBarChart;
